/**
 * The signed-in person's home in the sidebar.
 *
 * The compact card is always reachable below the scrolling room list. Opening
 * it reveals account actions; the two personal editors stay in dialogs so a
 * preference change never takes the reader away from their conversation.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  ChangeEvent,
  DragEvent as ReactDragEvent,
  FormEvent,
  KeyboardEvent as ReactKeyboardEvent,
  ReactElement,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/api/auth'
import { api, isApiError } from '@/api/client'
import { queryKeys } from '@/api/queries'
import { refreshCachedUser } from '@/api/store'
import type {
  CurrentUser,
  FieldErrors,
  NotificationPreference,
  ProfileImageUploadSession,
  UpdateProfileRequest,
} from '@/api/types'
import { Avatar, Button, Modal, Select, TextArea, TextField, useToast } from '@/components/ui'
import { useTheme } from '@/hooks/useTheme'
import { setExternalImages, useExternalImages } from '@/lib/externalImages'
import { diagnosticsReport } from '@/lib/diagnostics'
import type { ThemePreference } from '@/lib/theme'
import { InviteUsersDialog } from '@/components/InviteUsersDialog'
import styles from './AccountMenu.module.css'

type AccountDialog = 'profile' | 'settings' | 'invite' | null

const NOTIFICATION_OPTIONS: ReadonlyArray<{ value: NotificationPreference; label: string }> = [
  { value: 'all', label: 'All activity' },
  { value: 'mentions', label: 'Direct messages and mentions' },
  { value: 'nothing', label: 'Nothing' },
]

const THEME_OPTIONS: ReadonlyArray<{ value: ThemePreference; label: string }> = [
  { value: 'system', label: 'Match system' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
]

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ')
}

function personName(user: CurrentUser): string {
  const display = user.display_name.trim()
  return display === '' ? user.username : display
}

function messageFor(error: unknown): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message !== '') return error.message
  return 'The change could not be saved.'
}

function fieldError(errors: FieldErrors, field: string): string | null {
  return errors[field]?.[0] ?? null
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

function MenuChevron(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="m4 6 4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ProfileIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <circle cx="8" cy="5.25" r="2.25" stroke="currentColor" strokeWidth="1.4" />
      <path d="M3.75 13c.45-2.25 2-3.4 4.25-3.4s3.8 1.15 4.25 3.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

function SettingsIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path d="M3 4h10M3 8h10M3 12h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="6" cy="4" r="1.25" fill="var(--surface-raised)" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="10" cy="8" r="1.25" fill="var(--surface-raised)" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="7" cy="12" r="1.25" fill="var(--surface-raised)" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  )
}

function ShieldIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M8 2.5 3.5 4.5v4.2c0 2.8 1.9 4.7 4.5 5.8 2.6-1.1 4.5-3 4.5-5.8V4.5L8 2.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M6.2 8.1 7.4 9.3 9.8 6.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function AdminIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path d="M3 13V6.5L8 3l5 3.5V13M6 13V9h4v4M2.5 13h11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function SignOutIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path d="M7 3H3.5v10H7M9.5 5.5 12 8l-2.5 2.5M5.5 8H12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

interface ProfileDialogProps {
  open: boolean
  user: CurrentUser
  onClose: () => void
}

function ProfileDialog({ open, user, onClose }: ProfileDialogProps): ReactElement {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [displayName, setDisplayName] = useState(user.display_name)
  const [bio, setBio] = useState(user.bio ?? '')
  const [linkedinUrl, setLinkedinUrl] = useState(user.linkedin_url ?? '')
  const [phone, setPhone] = useState(user.phone ?? '')
  const [profileImage, setProfileImage] = useState<File | null>(null)
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null)
  const [removeImage, setRemoveImage] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [isImageDropTarget, setIsImageDropTarget] = useState(false)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const copyMyId = (): void => {
    if (typeof navigator.clipboard?.writeText === 'function') {
      void navigator.clipboard.writeText(user.id).then(
        () => toast.success('Relaii-ID kopiert til utklippstavlen!'),
        () => toast.error('Kunne ikke kopiere Relaii-ID.'),
      )
    } else {
      toast.warning('Nettleseren tillater ikke kopiering til utklippstavlen.')
    }
  }

  useEffect(() => {
    if (!open) return
    setDisplayName(user.display_name)
    setBio(user.bio ?? '')
    setLinkedinUrl(user.linkedin_url ?? '')
    setPhone(user.phone ?? '')
    setProfileImage(null)
    setRemoveImage(false)
    setUploadProgress(null)
    setIsImageDropTarget(false)
    setErrors({})
    setGeneralError(null)
  }, [open, user])

  useEffect(() => {
    if (profileImage === null || typeof URL.createObjectURL !== 'function') {
      setProfileImagePreview(null)
      return undefined
    }

    const preview = URL.createObjectURL(profileImage)
    setProfileImagePreview(preview)
    return () => URL.revokeObjectURL(preview)
  }, [profileImage])

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()

    const name = displayName.trim()
    const linkedIn = linkedinUrl.trim()
    const localErrors: FieldErrors = {}

    if (name === '') localErrors.display_name = ['Enter a display name.']
    if (linkedIn !== '' && !isHttpUrl(linkedIn)) {
      localErrors.linkedin_url = ['Enter a complete http:// or https:// URL.']
    }

    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors)
      return
    }

    setSaving(true)
    setErrors({})
    setGeneralError(null)

    const changes: UpdateProfileRequest = {
      display_name: name,
      bio: bio.trim() === '' ? null : bio.trim(),
      linkedin_url: linkedIn === '' ? null : linkedIn,
      phone: phone.trim() === '' ? null : phone.trim(),
    }

    try {
      let updated = await api.patch<CurrentUser>('/auth/me', changes)
      queryClient.setQueryData<CurrentUser>(queryKeys.me(), updated)

      if (removeImage && profileImage === null) {
        updated = await api.del<CurrentUser>('/auth/me/avatar')
      }

      if (profileImage !== null) {
        setUploadProgress(0)
        const session = await api.post<ProfileImageUploadSession>('/auth/me/avatar/uploads', {
          filename: profileImage.name,
          mime_type: profileImage.type,
          byte_size: profileImage.size,
        })
        await api.upload(
          session.upload.url,
          profileImage,
          session.upload.headers,
          setUploadProgress,
        )
        updated = await api.post<CurrentUser>(
          `/auth/me/avatar/uploads/${session.profile_image_upload_id}/finalize`,
          {},
        )
      }

      queryClient.setQueryData<CurrentUser>(queryKeys.me(), updated)
      if (profileImage !== null || removeImage) refreshCachedUser(updated)
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all() })
      onClose()
    } catch (error) {
      if (isApiError(error) && error.code === 'validation_failed') setErrors(error.fieldErrors)
      else setGeneralError(messageFor(error))
    } finally {
      setSaving(false)
    }
  }

  const selectProfileImage = (file: File | null): void => {
    if (file === null) return

    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/avif']
    const localErrors: FieldErrors = {}
    if (!allowed.includes(file.type)) {
      localErrors.profile_image = ['Choose a JPEG, PNG, GIF, WebP or AVIF image.']
    } else if (file.size > 5 * 1024 * 1024) {
      localErrors.profile_image = ['The profile image must be 5 MB or smaller.']
    } else if (file.size === 0) {
      localErrors.profile_image = ['Choose an image that is not empty.']
    }

    if (Object.keys(localErrors).length > 0) {
      setProfileImage(null)
      setErrors((current) => ({ ...current, ...localErrors }))
      return
    }

    setProfileImage(file)
    setRemoveImage(false)
    setUploadProgress(null)
    setErrors((current) => {
      const next = { ...current }
      delete next.profile_image
      return next
    })
  }

  const chooseProfileImage = (event: ChangeEvent<HTMLInputElement>): void => {
    selectProfileImage(event.target.files?.[0] ?? null)
    // Allow choosing the same file again after correcting a validation error.
    event.target.value = ''
  }

  const hasDroppedFiles = (event: ReactDragEvent<HTMLLabelElement>): boolean =>
    Array.from(event.dataTransfer.types).includes('Files')

  const onImageDragOver = (event: ReactDragEvent<HTMLLabelElement>): void => {
    if (saving || !hasDroppedFiles(event)) return
    event.preventDefault()
    setIsImageDropTarget(true)
  }

  const onImageDrop = (event: ReactDragEvent<HTMLLabelElement>): void => {
    event.preventDefault()
    setIsImageDropTarget(false)
    if (saving) return
    selectProfileImage(event.dataTransfer.files[0] ?? null)
  }

  const visibleAvatar = removeImage ? null : (profileImagePreview ?? user.avatar_url)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit profile"
      description="How other people see you on this server."
      size="md"
      closeOnOverlayClick={!saving}
      footer={
        <div className={styles.profileFooter}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form="relaii-profile-form" variant="primary" loading={saving}>
            Update profile
          </Button>
        </div>
      }
    >
      <form
        id="relaii-profile-form"
        className={styles.form}
        autoComplete="off"
        onSubmit={submit}
        noValidate
      >
        <div className={styles.profilePreview}>
          <Avatar
            name={displayName.trim() === '' ? user.username : displayName}
            id={user.id}
            src={visibleAvatar}
            size="lg"
            decorative
          />
          <span>
            <strong>{displayName.trim() === '' ? user.username : displayName}</strong>
            <small>{user.qualified_id}</small>
            <span className={styles.profileIdRow}>
              <code className={styles.profileIdCode} title={user.id}>
                {user.id}
              </code>
              <button
                type="button"
                className={styles.copyIdBtn}
                onClick={copyMyId}
                title="Kopier Relaii-ID"
              >
                <svg viewBox="0 0 16 16" width="12" height="12" fill="none" aria-hidden="true">
                  <rect x="5" y="5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
                  <path d="M3.5 10.5H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h6.5a1 1 0 0 1 1 1v.5" stroke="currentColor" strokeWidth="1.3" />
                </svg>
                Kopier ID
              </button>
            </span>
          </span>
        </div>

        {generalError !== null ? <p className={styles.error}>{generalError}</p> : null}

        <TextField
          label="Display name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          error={fieldError(errors, 'display_name')}
          maxLength={120}
          autoComplete="off"
          labelExtra={
            <span className={styles.characterCount} aria-live="polite">
              {displayName.length}/120
            </span>
          }
          required
        />
        <div className={styles.imageField}>
          <span className={styles.fieldLabel}>Profile image</span>
          <div className={styles.imageActions}>
            <label
              className={cx(
                styles.imageDropzone,
                isImageDropTarget && styles.imageDropzoneActive,
                saving && styles.imageDropzoneDisabled,
              )}
              onDragOver={onImageDragOver}
              onDragLeave={() => setIsImageDropTarget(false)}
              onDrop={onImageDrop}
            >
              <input
                type="file"
                aria-label="Profile image"
                accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
                onChange={chooseProfileImage}
                disabled={saving}
              />
              <span className={styles.imageDropzoneTitle}>
                {isImageDropTarget ? 'Drop image to upload' : 'Drop an image here'}
              </span>
              <span className={styles.imageDropzoneHint}>
                {profileImage === null ? 'or click to choose an image' : `Selected: ${profileImage.name}`}
              </span>
            </label>
            {user.avatar_url !== null && !removeImage ? (
              <Button type="button" onClick={() => { setProfileImage(null); setRemoveImage(true) }} disabled={saving}>
                Remove
              </Button>
            ) : null}
          </div>
          <small className={styles.fieldHint}>
            {profileImage?.name ?? (removeImage ? 'Your initials will be used.' : 'JPEG, PNG, GIF, WebP or AVIF. Maximum 5 MB.')}
          </small>
          {uploadProgress !== null ? (
            <div className={styles.uploadProgress} role="progressbar" aria-label="Profile image upload" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(uploadProgress * 100)}>
              <span style={{ width: `${Math.round(uploadProgress * 100)}%` }} />
            </div>
          ) : null}
          {fieldError(errors, 'profile_image') !== null ? (
            <small className={styles.fieldError}>{fieldError(errors, 'profile_image')}</small>
          ) : null}
        </div>
        <TextArea
          label="Bio"
          value={bio}
          onChange={(event) => setBio(event.target.value)}
          error={fieldError(errors, 'bio')}
          maxLength={1000}
          rows={4}
          placeholder="A little about you and what you work on."
          labelExtra={
            <span className={styles.characterCount} aria-live="polite">
              {bio.length}/1000
            </span>
          }
        />
        <TextField
          label="Email"
          type="email"
          value={user.email ?? ''}
          readOnly
          maxLength={320}
          hint="Your account email is managed by the server."
          autoComplete="off"
          labelExtra={
            <span className={styles.characterCount} aria-live="polite">
              {(user.email ?? '').length}/320
            </span>
          }
        />
        <TextField
          label="LinkedIn"
          type="url"
          value={linkedinUrl}
          onChange={(event) => setLinkedinUrl(event.target.value)}
          error={fieldError(errors, 'linkedin_url')}
          placeholder="https://www.linkedin.com/in/your-name"
          maxLength={500}
          inputMode="url"
          autoComplete="off"
          labelExtra={
            <span className={styles.characterCount} aria-live="polite">
              {linkedinUrl.length}/500
            </span>
          }
        />
        <TextField
          label="Phone"
          type="tel"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          error={fieldError(errors, 'phone')}
          maxLength={50}
          placeholder="+47 000 00 000"
          autoComplete="off"
          labelExtra={
            <span className={styles.characterCount} aria-live="polite">
              {phone.length}/50
            </span>
          }
        />
      </form>
    </Modal>
  )
}

interface SettingsDialogProps {
  open: boolean
  user: CurrentUser
  onClose: () => void
}

/**
 * Whether this browser will show a notification when something arrives while
 * the window is in the background.
 *
 * The permission can only be asked for from something the person did, which is
 * why it is a button rather than a prompt on load. In the desktop client the
 * answer is already yes, and the row says so instead.
 */
function DesktopNotifications(): ReactElement | null {
  const supported = typeof Notification !== 'undefined'
  const [permission, setPermission] = useState<NotificationPermission | null>(
    supported ? Notification.permission : null,
  )

  if (!supported || permission === null) return null

  return (
    <div className={styles.notifications}>
      <span className={styles.notificationsLabel}>Notifications on this device</span>
      {permission === 'granted' ? (
        <span className={styles.notificationsState}>
          On. A notification names the conversation, never the message.
        </span>
      ) : permission === 'denied' ? (
        <span className={styles.notificationsState}>
          Blocked here. Allow notifications for this site in your browser or system settings.
        </span>
      ) : (
        <Button
          size="sm"
          onClick={() => {
            void Notification.requestPermission().then((result) => setPermission(result))
          }}
        >
          Turn on notifications
        </Button>
      )}
    </div>
  )
}

function SettingsDialog({ open, user, onClose }: SettingsDialogProps): ReactElement {
  const queryClient = useQueryClient()
  const { preference: themePreference, setPreference: setThemePreference } = useTheme()
  const externalImages = useExternalImages()
  const [diagnostics, setDiagnostics] = useState<string | null>(null)
  const [timezone, setTimezone] = useState(user.timezone ?? '')
  const [preference, setPreference] = useState<NotificationPreference>(
    user.notification_preference ?? 'all',
  )
  const [errors, setErrors] = useState<FieldErrors>({})
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setTimezone(user.timezone ?? '')
    setPreference(user.notification_preference ?? 'all')
    setErrors({})
    setGeneralError(null)
  }, [open, user])

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    setSaving(true)
    setErrors({})
    setGeneralError(null)

    const changes: UpdateProfileRequest = {
      timezone: timezone.trim() === '' ? null : timezone.trim(),
      notification_preference: preference,
    }

    try {
      const updated = await api.patch<CurrentUser>('/auth/me', changes)
      queryClient.setQueryData<CurrentUser>(queryKeys.me(), updated)
      onClose()
    } catch (error) {
      if (isApiError(error) && error.code === 'validation_failed') setErrors(error.fieldErrors)
      else setGeneralError(messageFor(error))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Your settings"
      description="Personal defaults for this account."
      size="sm"
      closeOnOverlayClick={!saving}
      footer={
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form="relaii-settings-form" variant="primary" loading={saving}>
            Save settings
          </Button>
        </>
      }
    >
      <form id="relaii-settings-form" className={styles.form} onSubmit={submit} noValidate>
        {generalError !== null ? <p className={styles.error}>{generalError}</p> : null}

        <Select
          label="Notify me about"
          value={preference}
          onChange={(event) => setPreference(event.target.value as NotificationPreference)}
          options={NOTIFICATION_OPTIONS}
          error={fieldError(errors, 'notification_preference')}
        />
        <Select
          label="Appearance"
          value={themePreference}
          onChange={(event) => setThemePreference(event.target.value as ThemePreference)}
          options={THEME_OPTIONS}
          hint="Choose light, dark, or follow your device setting. Saved for this browser on your account."
        />
        <TextField
          label="Time zone"
          value={timezone}
          onChange={(event) => setTimezone(event.target.value)}
          error={fieldError(errors, 'timezone')}
          placeholder="Europe/Oslo"
          hint="Use an IANA time zone such as Europe/Oslo. Other people in this Space see it as your local time on your card. Leave blank to share nothing."
          autoComplete="off"
          spellCheck={false}
        />
        <DesktopNotifications />
        <Button variant="ghost" onClick={() => setDiagnostics(diagnosticsReport())}>Show device diagnostics</Button>
        {diagnostics !== null ? <TextArea label="Device diagnostics" readOnly value={diagnostics} rows={6} hint="Only event types and timestamps. No messages, account IDs, tokens or URLs. Nothing is uploaded automatically." /> : null}
        <Select label="External webhook images" value={externalImages ? 'allow' : 'ask'} onChange={(event) => setExternalImages(event.target.value === 'allow')} options={[
          { value: 'ask', label: 'Ask before loading (recommended)' },
          { value: 'allow', label: 'Always load on this device' },
        ]} hint="External image hosts can see your IP address when an image loads." />
      </form>
    </Modal>
  )
}

export interface AccountMenuProps {
  /** Closes the slide-over navigation after following the Administration link. */
  onNavigate?: () => void
}

export function AccountMenu({ onNavigate }: AccountMenuProps): ReactElement | null {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [dialog, setDialog] = useState<AccountDialog>(null)
  const [signingOut, setSigningOut] = useState(false)

  useEffect(() => {
    if (!menuOpen) return undefined

    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus()

    const onPointerDown = (event: PointerEvent): void => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) {
        setMenuOpen(false)
      }
    }

    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [menuOpen])

  const closeMenu = useCallback((): void => {
    setMenuOpen(false)
    triggerRef.current?.focus()
  }, [])

  const openDialog = (next: Exclude<AccountDialog, null>): void => {
    closeMenu()
    setDialog(next)
  }

  const openAdmin = (): void => {
    setMenuOpen(false)
    onNavigate?.()
    navigate('/admin')
  }

  const openSecurity = (): void => {
    setMenuOpen(false)
    onNavigate?.()
    navigate('/account')
  }

  const signOut = async (): Promise<void> => {
    setSigningOut(true)
    setMenuOpen(false)
    try {
      await logout()
      navigate('/login', { replace: true })
    } finally {
      setSigningOut(false)
    }
  }

  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])
    if (items.length === 0) return

    if (event.key === 'Escape') {
      event.preventDefault()
      closeMenu()
      return
    }

    const current = items.indexOf(document.activeElement as HTMLButtonElement)
    let next = -1
    if (event.key === 'ArrowDown') next = (current + 1) % items.length
    else if (event.key === 'ArrowUp') next = (current - 1 + items.length) % items.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = items.length - 1
    else return

    event.preventDefault()
    items[next]?.focus()
  }

  if (user === null) return null

  const name = personName(user)
  const mayAdminister = user.is_admin || user.is_owner

  return (
    <>
      {dialog === 'invite' && mayAdminister ? <InviteUsersDialog onClose={() => setDialog(null)} /> : null}
      <div className={styles.account} ref={rootRef}>
        {menuOpen ? (
          <div
            ref={menuRef}
            className={styles.menu}
            role="menu"
            aria-label="Account"
            onKeyDown={onMenuKeyDown}
          >
            <button className={styles.menuItem} type="button" role="menuitem" onClick={() => openDialog('profile')}>
              <ProfileIcon />
              <span>Edit profile</span>
            </button>
            <button className={styles.menuItem} type="button" role="menuitem" onClick={() => openDialog('settings')}>
              <SettingsIcon />
              <span>Settings</span>
            </button>
            <button className={styles.menuItem} type="button" role="menuitem" onClick={openSecurity}>
              <ShieldIcon />
              <span>Two-factor authentication</span>
            </button>
            {mayAdminister ? <button className={styles.menuItem} type="button" role="menuitem" onClick={() => openDialog('invite')}>
              <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
                <path d="M13 8V4H3v8h5M3 4l5 4 5-4M12 9v5M9.5 11.5h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Invite users</span>
            </button> : null}
            {mayAdminister ? (
              <button className={styles.menuItem} type="button" role="menuitem" onClick={openAdmin}>
                <AdminIcon />
                <span>Administration</span>
              </button>
            ) : null}
            <div className={styles.separator} role="separator" />
            <button
              className={`${styles.menuItem} ${styles.danger}`}
              type="button"
              role="menuitem"
              onClick={() => void signOut()}
              disabled={signingOut}
            >
              <SignOutIcon />
              <span>{signingOut ? 'Signing out…' : 'Sign out'}</span>
            </button>
          </div>
        ) : null}

        <button
          ref={triggerRef}
          className={styles.trigger}
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((current) => !current)}
        >
          <Avatar
            name={name}
            id={user.id}
            src={user.avatar_url}
            size="sm"
            presence="online"
            decorative
          />
          <span className={styles.identity}>
            <span className={styles.name}>{name}</span>
            <span className={styles.handle}>{user.qualified_id}</span>
          </span>
          <span className={styles.menuChevron} aria-hidden="true">
            <MenuChevron />
          </span>
        </button>
      </div>

      <ProfileDialog open={dialog === 'profile'} user={user} onClose={() => setDialog(null)} />
      <SettingsDialog open={dialog === 'settings'} user={user} onClose={() => setDialog(null)} />
    </>
  )
}

export default AccountMenu
